/**
 * Admin menu (ADMIN_REDESIGN_PLAN.md): a Square-style sidebar on desktop and a slide-out panel on phones.
 * Each menu item opens one page; pages that grew out of several old sections (Compliance, Limits,
 * wallets & fees, Earn & Yield, Settings) show those sections as tabs, and `?tab=` keeps naming the
 * section so old links still work.
 */
import { useEffect, useState } from 'react';
import { BRAND, D } from '@platform/shared';
import { cx } from '../components/ui';
import { api } from '../lib/api';
import { navigate } from '../lib/router';
import { logout, useMe } from '../stores/session';

export type Tab =
  | 'home'
  | 'online'
  | 'risk'
  | 'summary'
  | 'accounts'
  | 'approvals'
  | 'staff'
  | 'audit'
  | 'users'
  | 'kyc'
  | 'sof'
  | 'rollout'
  | 'withdrawals'
  | 'limits'
  | 'verifywallets'
  | 'fees'
  | 'deposits'
  | 'support'
  | 'positions'
  | 'instruments'
  | 'feeds'
  | 'providers'
  | 'earn'
  | 'yield'
  | 'academy'
  | 'crypto_pro'
  | 'hedging'
  | 'exposure'
  | 'push'
  | 'webhooks'
  | 'meme'
  | 'features'
  | 'copy'
  | 'masters'
  | 'portfolios'
  | 'guide'
  | 'signin';

/** Each section's name, as its tab and as "where" in Online now. */
export const TAB_LABEL: Record<Tab, string> = {
  home: 'Home',
  online: 'Online now',
  risk: 'Risk desk',
  summary: 'Daily summary',
  accounts: 'Accounts',
  approvals: 'Approvals',
  staff: 'Staff & roles',
  audit: 'Audit log',
  users: 'Users',
  kyc: 'KYC',
  sof: 'Source of funds',
  rollout: 'SOF roll-out',
  withdrawals: 'Withdrawals',
  limits: 'Withdrawal limits',
  verifywallets: 'Verification wallets',
  fees: 'Fees',
  deposits: 'Deposits',
  support: 'Support',
  positions: 'Positions',
  instruments: 'Instruments',
  feeds: 'Price feeds',
  providers: 'Data providers',
  earn: 'Earn program',
  yield: 'Yield',
  academy: 'Academy',
  crypto_pro: 'Crypto Pro',
  hedging: 'Hedging',
  exposure: 'Exposure',
  push: 'Notifications',
  webhooks: 'Webhooks',
  meme: 'Meme coins',
  features: 'Features',
  copy: 'Copy trading',
  masters: 'Master accounts',
  portfolios: 'Smart portfolios',
  guide: 'Guide',
  signin: 'My sign-in security',
};

const ICON = {
  home: 'M4 10.5L12 4l8 6.5V20h-5v-6H9v6H4z',
  approvals: 'M5 4h14v16H5zM9 12l2 2 4-4',
  online: 'M3 12h4l3-7 4 14 3-7h4',
  users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 12 0v1M16 3.5a4 4 0 0 1 0 7M22 21v-1a6 6 0 0 0-4-5.6',
  accounts: 'M4 5h16v14H4zM8 10h3M8 14h8',
  compliance: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  support: 'M4 5h16v11H8l-4 4z',
  deposits: 'M12 4v12M7 11l5 5 5-5M4 20h16',
  withdrawals: 'M12 20V8M7 13l5-5 5 5M4 4h16',
  limits: 'M4 7h16M4 12h16M4 17h10',
  positions: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  exposure: 'M12 3v18M5 7h14M3 13l2-6 2 6a2 2 0 0 1-4 0zM17 13l2-6 2 6a2 2 0 0 1-4 0z',
  hedging: 'M7 7h10v10H7zM3 12h4M17 12h4',
  instruments: 'M20 12l-8 8-9-9V3h8zM7.5 7.5h.01',
  feeds: 'M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16M5 19h.01',
  earn: 'M12 3v18M17 6.5c-1-1-2.5-1.5-5-1.5-3 0-4.5 1.5-4.5 3.5S9 11.5 12 12s4.5 1.5 4.5 3.5S15 19 12 19c-2.5 0-4-.5-5-1.5',
  copy: 'M8 8h12v12H8zM4 16V4h12',
  masters: 'M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8L3.5 9.2l5.9-.9z',
  portfolios: 'M12 3a9 9 0 1 0 9 9h-9z',
  cryptopro: 'M13 3L5 13h6l-1 8 8-10h-6z',
  meme: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM9 10h.01M15 10h.01M8.5 14.5a4 4 0 0 0 7 0',
  academy: 'M2 9l10-5 10 5-10 5zM6 11v5c3 2 9 2 12 0v-5',
  risk: 'M12 4l9 16H3zM12 10v4M12 17h.01',
  summary: 'M6 3h9l4 4v14H6zM9 12h7M9 16h7',
  book: 'M3 5h6a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H3zM21 5h-6a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h7z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 21h4',
  chat: 'M4 5h11v8H8l-4 3zM9 16v1h7l4 3V9h-3',
  back: 'M15 18l-6-6 6-6',
  logout: 'M15 4h4v16h-4M10 17l5-5-5-5M15 12H3',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
} as const;

export function NavIcon({ d, size = 18 }: { d: keyof typeof ICON; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICON[d]} />
    </svg>
  );
}

type Badge = 'approvals' | 'compliance' | 'support' | 'withdrawals';
interface Item {
  id: string;
  label: string;
  icon: keyof typeof ICON;
  tabs: Tab[];
  badge?: Badge;
}
const item = (id: string, label: string, icon: keyof typeof ICON, tabs: Tab[], badge?: Badge): Item => ({ id, label, icon, tabs, badge });

const ADMIN_NAV: { title: string; items: Item[] }[] = [
  { title: '', items: [item('home', 'Home', 'home', ['home']), item('approvals', 'Approvals', 'approvals', ['approvals'], 'approvals'), item('online', 'Online now', 'online', ['online'])] },
  {
    title: 'Clients',
    items: [
      item('users', 'Users', 'users', ['users']),
      item('compliance', 'Compliance', 'compliance', ['kyc', 'sof', 'rollout'], 'compliance'),
      item('support', 'Support', 'support', ['support'], 'support'),
    ],
  },
  {
    title: 'Dealer desk',
    items: [
      item('accounts', 'Accounts', 'accounts', ['accounts']),
      item('masters', 'Master accounts', 'masters', ['masters']),
      item('risk', 'Risk desk', 'risk', ['risk']),
      item('summary', 'Daily summary', 'summary', ['summary']),
      item('guide', 'Guide', 'book', ['guide']),
      item('settings', 'Settings', 'settings', ['staff', 'audit', 'features', 'providers', 'push', 'webhooks']),
      item('signin', 'My sign-in security', 'compliance', ['signin']),
    ],
  },
  {
    title: 'Money',
    items: [
      item('deposits', 'Deposits', 'deposits', ['deposits']),
      item('withdrawals', 'Withdrawals', 'withdrawals', ['withdrawals'], 'withdrawals'),
      item('moneyset', 'Limits, wallets & fees', 'limits', ['limits', 'verifywallets', 'fees']),
    ],
  },
  {
    title: 'Trading',
    items: [
      item('positions', 'Positions', 'positions', ['positions']),
      item('exposure', 'Exposure', 'exposure', ['exposure']),
      item('hedging', 'Hedging', 'hedging', ['hedging']),
      item('instruments', 'Instruments', 'instruments', ['instruments']),
      item('feeds', 'Price feeds', 'feeds', ['feeds']),
    ],
  },
  {
    title: 'Products',
    items: [
      item('earn', 'Earn & Yield', 'earn', ['earn', 'yield']),
      item('copy', 'Copy trading', 'copy', ['copy']),
      item('portfolios', 'Smart portfolios', 'portfolios', ['portfolios']),
      item('crypto_pro', 'Crypto Pro', 'cryptopro', ['crypto_pro']),
      item('meme', 'Meme coins', 'meme', ['meme']),
      item('academy', 'Academy', 'academy', ['academy']),
    ],
  },
];

/** All a dealer sees (DEALER_ROLE_PLAN.md, ADMIN_REDESIGN_PLAN.md). The API refuses the others too. */
const DEALER_NAV: { title: string; items: Item[] }[] = [
  { title: '', items: [item('home', 'Home', 'home', ['home'])] },
  {
    title: 'Dealer desk',
    items: [
      item('accounts', 'Accounts', 'accounts', ['accounts']),
      item('masters', 'Master accounts', 'masters', ['masters']),
      item('risk', 'Risk desk', 'risk', ['risk']),
      item('guide', 'Guide', 'book', ['guide']),
    ],
  },
  { title: 'Dealing', items: [item('positions', 'Positions', 'positions', ['positions']), item('exposure', 'Exposure', 'exposure', ['exposure']), item('hedging', 'Hedging', 'hedging', ['hedging'])] },
  { title: 'Me', items: [item('signin', 'My sign-in security', 'compliance', ['signin'])] },
];

export const ALL_TABS = Object.keys(TAB_LABEL) as Tab[];
export const DEALER_TABS: Tab[] = DEALER_NAV.flatMap((g) => g.items.flatMap((i) => i.tabs));

/** The menu for this login, without the sections in `hidden`. */
export function navFor(dealer: boolean, hidden: Tab[]) {
  return (dealer ? DEALER_NAV : ADMIN_NAV)
    .map((g) => ({ ...g, items: g.items.map((i) => ({ ...i, tabs: i.tabs.filter((t) => !hidden.includes(t)) })).filter((i) => i.tabs.length > 0) }))
    .filter((g) => g.items.length > 0);
}
export type NavGroup = ReturnType<typeof navFor>[number];
export type NavItem = NavGroup['items'][number];

/** The menu item a section belongs to. */
export const itemOf = (nav: NavGroup[], tab: Tab) => nav.flatMap((g) => g.items).find((i) => i.tabs.includes(tab));

export interface NavStats {
  badges: Partial<Record<Badge, number>>;
  /** 0–1 for the bar under the name, with its label. */
  progress: { value: number; label: string } | null;
}

/** Counts on the menu and the progress bar: the admin's queues, or how much of the hedge target a dealer has reached. */
export function useNavStats(dealer: boolean): NavStats {
  const [stats, setStats] = useState<NavStats>({ badges: {}, progress: null });
  useEffect(() => {
    let gone = false;
    const load = async () => {
      try {
        if (dealer) {
          const r = await api.get<{ exposure: { hedgeTargetNotional: string | null; unhedgedNotional: string | null }[] }>('/api/admin/desk/risk');
          let target = D.from('0');
          let open = D.from('0');
          for (const e of r.exposure) {
            if (e.hedgeTargetNotional) target = target.add(D.from(e.hedgeTargetNotional).abs());
            if (e.unhedgedNotional) open = open.add(D.from(e.unhedgedNotional).abs());
          }
          const value = target.isZero() ? 1 : Math.max(0, Math.min(1, 1 - open.div(target).toNumber()));
          if (!gone) setStats({ badges: {}, progress: { value, label: 'Hedge target reached' } });
        } else {
          const h = await api.get<AdminHomeData>('/api/admin/home');
          const q = h.queues;
          const total = q.waiting + q.doneToday;
          if (!gone)
            setStats({
              badges: { approvals: q.approvals, compliance: q.kyc + q.sof, support: q.support.count, withdrawals: q.withdrawals.count },
              progress: { value: total === 0 ? 1 : q.doneToday / total, label: "Today's queue cleared" },
            });
        }
      } catch {
        /* the menu works without counts */
      }
    };
    void load();
    const t = window.setInterval(load, 60_000);
    return () => {
      gone = true;
      window.clearInterval(t);
    };
  }, [dealer]);
  return stats;
}

export interface AdminHomeData {
  asOf: string;
  /** Over `[from, to)`, compared with `[previousFrom, from)`. */
  money: {
    from: string;
    to: string;
    previousFrom: string;
    ftd: { count: number; amount: string; previous: number; newClients: number; fromNewClients: number };
    deposits: { amount: string; count: number; previous: string };
    withdrawalsPaid: { amount: string; previous: string };
  };
  queues: {
    withdrawals: { count: number; amount: string; oldestAt: string | null };
    kyc: number;
    sof: number;
    approvals: number;
    support: { count: number; oldestAt: string | null };
    waiting: number;
    doneToday: number;
  };
  trading: {
    today: { volume: string; trades: number; newClients: number; netDeposits: string };
    yesterday: { volume: string; trades: number; newClients: number; netDeposits: string };
    openPositions: number;
    hours: { hour: number; today: string; yesterday: string }[];
  };
}

/** Leaves Admin for the trading platform (admins only). */
function BackToPlatform({ className, short }: { className?: string; short?: boolean }) {
  return (
    <a href="/" onClick={(e) => (e.preventDefault(), navigate('/'))} className={cx('flex items-center justify-center gap-2 rounded-full px-4 font-semibold text-fg hover:bg-[#efefef]', className)}>
      <NavIcon d="back" size={18} />
      {short ? 'Platform' : 'Back to platform'}
    </a>
  );
}

/** Signs a dealer out: a dealer login has no trading platform to go back to (App.tsx keeps it on /admin). */
function LogOut({ className }: { className?: string }) {
  return (
    <button type="button" onClick={() => void logout().then(() => navigate('/login'))} className={cx('flex items-center justify-center gap-2 rounded-full px-4 font-semibold text-fg hover:bg-[#efefef]', className)}>
      <NavIcon d="logout" size={18} />
      Log out
    </button>
  );
}

function Sidebar({ nav, tab, onPick, stats, dealer, onClose }: { nav: NavGroup[]; tab: Tab; onPick: (t: Tab) => void; stats: NavStats; dealer: boolean; onClose?: () => void }) {
  const me = useMe();
  const current = itemOf(nav, tab);
  const pct = stats.progress ? Math.round(stats.progress.value * 100) : null;
  return (
    <nav aria-label="Admin sections" className="flex h-full w-full flex-col gap-0.5 overflow-y-auto px-3 pt-5 pb-4">
      <div className="flex items-center gap-3 px-2.5 pb-5">
        <span aria-hidden="true" className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-[#6b5328] text-[15px] font-bold text-white italic">
          {BRAND.name.slice(0, 2).toUpperCase()}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-semibold">{BRAND.name}</span>
          <span className="truncate text-[13px] text-muted">
            {dealer ? 'Dealer' : 'Admin'} · {me?.staffName}
          </span>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close menu" className="grid size-10 place-items-center rounded-full">
            <NavIcon d="close" />
          </button>
        )}
      </div>
      {pct !== null && (
        <div className="flex flex-col gap-2 px-2.5 pb-5">
          <span className="h-2 overflow-hidden rounded bg-[#e2e2e2]">
            <span className="block h-2 rounded bg-fg" style={{ width: `${pct}%` }} />
          </span>
          <span className="flex justify-between text-sm text-muted">
            <span>{stats.progress!.label}</span>
            <span className="num">{pct}%</span>
          </span>
        </div>
      )}
      {/* An admin can go back to the trading platform; a dealer login has none, so it signs out from the same place. */}
      {dealer ? <LogOut className="mb-4 h-12 shrink-0 border border-[#e2e2e2] text-[15px]" /> : <BackToPlatform className="mb-4 h-12 shrink-0 border border-[#e2e2e2] text-[15px]" />}
      {nav.map((g) => (
        <div key={g.title || 'top'} className="flex flex-col gap-0.5 pb-4">
          {g.title && <span className="px-2.5 pb-1.5 text-[13px] text-muted">{g.title}</span>}
          {g.items.map((i) => {
            const on = current?.id === i.id;
            const n = i.badge ? stats.badges[i.badge] : undefined;
            return (
              <button
                key={i.id}
                type="button"
                aria-current={on ? 'page' : undefined}
                onClick={() => onPick(i.tabs[0]!)}
                className={cx('flex h-[42px] items-center gap-3 rounded-[10px] px-2.5 text-left text-[15px] text-fg', on ? 'bg-[#e8e8e8] font-medium' : 'hover:bg-[#efefef]')}
              >
                <NavIcon d={i.icon} />
                <span className="flex-1 truncate">{i.label}</span>
                {!!n && <span className="num text-[13px] text-muted">{n}</span>}
              </button>
            );
          })}
        </div>
      ))}
      <div className="min-h-4 flex-1" />
      {!dealer && (
        <div className="flex justify-center gap-7 pt-2 pb-3.5">
          <button type="button" aria-label="Approvals" title="Approvals" onClick={() => onPick('approvals')} className="relative grid size-9 place-items-center">
            <NavIcon d="bell" size={20} />
            {!!stats.badges.approvals && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-down" />}
          </button>
          <button type="button" aria-label="Support" title="Support" onClick={() => onPick('support')} className="grid size-9 place-items-center">
            <NavIcon d="chat" size={20} />
          </button>
        </div>
      )}
    </nav>
  );
}

/** The sidebar on desktop; on phones a top bar whose menu button slides the same sidebar in. */
export function AdminNav({ nav, tab, onPick, title, dealer }: { nav: NavGroup[]; tab: Tab; onPick: (t: Tab) => void; title: string; dealer: boolean }) {
  const stats = useNavStats(dealer);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);
  const pick = (t: Tab) => {
    setOpen(false);
    onPick(t);
  };
  return (
    <>
      <aside className="hidden w-64 shrink-0 lg:block">
        <Sidebar nav={nav} tab={tab} onPick={pick} stats={stats} dealer={dealer} />
      </aside>
      <div className="sticky top-0 z-30 flex h-14 items-center gap-2 bg-bg px-2 lg:hidden">
        <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} className="grid size-11 place-items-center rounded-full">
          <NavIcon d="menu" size={20} />
        </button>
        <span className="flex-1 text-[15px] font-semibold">{title}</span>
        {dealer ? <LogOut className="h-10 text-sm" /> : <BackToPlatform short className="h-10 text-sm" />}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Admin menu">
          <button type="button" aria-label="Close menu" className="absolute inset-0 bg-scrim" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[min(300px,86vw)] bg-bg shadow-xl">
            <Sidebar nav={nav} tab={tab} onPick={pick} stats={stats} dealer={dealer} onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
